/**
 * Iter75-76: Billing-Seite — Tarif-Übersicht + Stripe-Upgrade-Flow.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Crown,
  Sparkles,
  Check,
  Loader2,
  CreditCard,
  RotateCw,
  AlertCircle,
  Receipt,
  XCircle,
  TrendingUp,
} from "lucide-react";

const TIER_ICONS = {
  free: { icon: Sparkles, tone: "text-slate-500" },
  starter: { icon: Sparkles, tone: "text-sky-600" },
  pro: { icon: Crown, tone: "text-violet-600" },
  enterprise: { icon: Crown, tone: "text-amber-600" },
};

const POLL_INTERVAL = 2000;
const MAX_POLL = 30; // 60s max

export default function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");
  const free = searchParams.get("free");

  const companyId = localStorage.getItem("demoCompanyId");

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollingResult, setPollingResult] = useState(null);
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [proQuotes, setProQuotes] = useState({}); // {plan_id: amount}

  const loadAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [plansRes, subRes, invRes] = await Promise.all([
        apiClient.get("/billing/plans"),
        apiClient.get(`/companies/${companyId}/subscription`),
        apiClient.get(`/companies/${companyId}/billing/invoices`),
      ]);
      setPlans(plansRes.data || []);
      setSubscription(subRes.data || null);
      setInvoices(invRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Tarife konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Iter95: Pro-Rated Quotes für alle Tarife laden wenn User auf zahlpflichtigem Plan
  useEffect(() => {
    if (!companyId || !subscription || subscription.plan_id === "free") {
      setProQuotes({});
      return;
    }
    const ordering = ["free", "starter", "pro", "enterprise"];
    const currentIdx = ordering.indexOf(subscription.plan_id);
    const higherPlans = ordering.slice(currentIdx + 1);
    if (higherPlans.length === 0) return;

    Promise.all(higherPlans.map(p =>
      apiClient.get(`/companies/${companyId}/billing/prorated-quote?to_plan_id=${p}`)
        .then(r => [p, r.data])
        .catch(() => [p, null])
    )).then(pairs => {
      const map = {};
      pairs.forEach(([k, v]) => { if (v) map[k] = v; });
      setProQuotes(map);
    });
  }, [companyId, subscription]);

  const startProRatedUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const res = await apiClient.post(`/companies/${companyId}/billing/prorated-upgrade`, {
        to_plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Pro-Rated Upgrade fehlgeschlagen");
      setUpgrading(null);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm(
      `Abo wirklich kündigen?\n\nDu behältst Zugriff bis ${fmtDate(subscription?.paid_until)}.`
    )) return;
    try {
      const r = await apiClient.post(`/companies/${companyId}/billing/cancel`);
      toast.success(r.data?.message || "Abo gekündigt");
      loadAll();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Kündigung fehlgeschlagen");
    }
  };

  // Poll bei Rückkehr von Stripe
  useEffect(() => {
    if (!sessionId || polling) return;
    let attempts = 0;
    setPolling(true);
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts >= MAX_POLL) {
        setPolling(false);
        if (!cancelled) toast.error("Zahlungsstatus-Prüfung läuft aus — bitte Seite neu laden.");
        return;
      }
      attempts += 1;
      try {
        const res = await apiClient.get(`/billing/checkout-status/${sessionId}`);
        const { payment_status, status, plan_id } = res.data;
        setPollingResult(res.data);
        if (payment_status === "paid") {
          toast.success(`🎉 ${plan_id?.toUpperCase()}-Tarif aktiviert!`);
          setPolling(false);
          await loadAll();
          // URL bereinigen
          setSearchParams({});
          return;
        }
        if (status === "expired" || status === "canceled") {
          toast.error("Zahlung abgebrochen");
          setPolling(false);
          setSearchParams({});
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        console.error(e);
        setPolling(false);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line

  // URL-Effekte
  useEffect(() => {
    if (cancelled) {
      toast.info("Zahlung abgebrochen");
      setSearchParams({});
    }
    if (free) {
      toast.success("Free-Tarif ist aktiv");
      setSearchParams({});
      loadAll();
    }
  }, [cancelled, free]); // eslint-disable-line

  const startCheckout = async (planId) => {
    if (!companyId) {
      toast.error("Keine Firma ausgewählt");
      return;
    }
    setUpgrading(planId);
    try {
      const res = await apiClient.post("/billing/checkout", {
        plan_id: planId,
        company_id: companyId,
        origin_url: window.location.origin,
        billing_cycle: planId === "free" ? "monthly" : billingCycle,
      });
      if (res.data?.url) {
        // Redirect zur Stripe-Checkout-Seite (oder Free-Confirmation)
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Checkout fehlgeschlagen");
      setUpgrading(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Abo & Tarife</h1>
        <p className="mt-1 text-sm text-slate-500">
          Wähle den passenden Tarif für deine Firma. Wechsle jederzeit — Stripe-Test-Modus.
        </p>
      </div>

      {/* Current Subscription Banner */}
      {subscription && (
        <Card className="p-4 border-slate-200 bg-gradient-to-r from-slate-50 to-violet-50" data-testid="billing-current-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500">Aktueller Tarif</div>
                <div className="text-base font-semibold text-slate-900" data-testid="billing-current-plan">
                  {subscription.plan_name}
                  {subscription.is_active && subscription.plan_id !== "free" && (
                    <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full uppercase tracking-wide">
                      aktiv
                    </span>
                  )}
                  {!subscription.is_active && subscription.plan_id !== "free" && (
                    <span className="ml-2 text-[10px] bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full uppercase tracking-wide">
                      abgelaufen
                    </span>
                  )}
                </div>
                {subscription.paid_until && (
                  <div className="text-xs text-slate-500 mt-0.5">
                    Bezahlt bis: <span className="font-medium">{fmtDate(subscription.paid_until)}</span>
                    {subscription.billing_cycle && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide bg-slate-100 px-1.5 py-0.5 rounded">
                        {subscription.billing_cycle === "yearly" ? "Jährlich" : "Monatlich"}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadAll}
                disabled={loading}
                data-testid="billing-refresh-btn"
              >
                <RotateCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Status prüfen
              </Button>
              {subscription.plan_id !== "free" && subscription.is_active && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelSubscription}
                  className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  data-testid="billing-cancel-btn"
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Kündigen
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Polling Banner */}
      {polling && (
        <Card className="p-4 border-sky-200 bg-sky-50" data-testid="billing-polling-card">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-sky-600 animate-spin" />
            <div>
              <div className="text-sm font-semibold text-sky-900">Zahlung wird verarbeitet …</div>
              <div className="text-xs text-sky-700">Wir prüfen den Status bei Stripe — bitte warten.</div>
            </div>
          </div>
        </Card>
      )}

      {/* Monthly/Yearly Toggle */}
      <div className="flex items-center justify-center" data-testid="billing-cycle-toggle">
        <div className="inline-flex rounded-full bg-slate-100 p-1 border border-slate-200">
          <button
            type="button"
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all ${
              billingCycle === "monthly"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="billing-cycle-monthly"
          >
            Monatlich
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle("yearly")}
            className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${
              billingCycle === "yearly"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid="billing-cycle-yearly"
          >
            Jährlich
            <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">
              −2 Monate
            </span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const isCurrent = subscription?.plan_id === plan.id && subscription?.is_active;
          const meta = TIER_ICONS[plan.id] || TIER_ICONS.free;
          const Icon = meta.icon;
          const quote = proQuotes[plan.id];
          const showProRated = !!quote && quote.prorated_amount_chf > 0 && quote.prorated_amount_chf < plan.price_chf && plan.id !== "free";
          return (
            <Card
              key={plan.id}
              className={`p-5 flex flex-col transition-all ${
                isCurrent
                  ? "border-violet-400 ring-2 ring-violet-100 shadow-md"
                  : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
              }`}
              data-testid={`billing-plan-card-${plan.id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`h-5 w-5 ${meta.tone}`} />
                <h3 className="text-base font-semibold text-slate-900">{plan.name}</h3>
                {showProRated && (
                  <span className="ml-auto bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                    Pro-Rated
                  </span>
                )}
              </div>

              <div className="mb-4">
                {plan.price_chf === 0 ? (
                  <>
                    <span className="text-3xl font-bold tabular-nums text-slate-900">CHF 0</span>
                  </>
                ) : billingCycle === "yearly" ? (
                  <>
                    <span className="text-3xl font-bold tabular-nums text-slate-900">
                      CHF {(plan.price_chf * 10).toFixed(0)}
                    </span>
                    <span className="text-sm text-slate-500"> /Jahr</span>
                    <div className="text-[11px] text-emerald-700 mt-0.5">
                      ≙ CHF {plan.price_chf.toFixed(0)}/Monat · spare CHF {(plan.price_chf * 2).toFixed(0)}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-3xl font-bold tabular-nums text-slate-900">
                      CHF {plan.price_chf.toFixed(0)}
                    </span>
                    <span className="text-sm text-slate-500"> /Monat</span>
                  </>
                )}
              </div>

              <ul className="space-y-1.5 mb-5 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                    <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => showProRated ? startProRatedUpgrade(plan.id) : startCheckout(plan.id)}
                disabled={!!upgrading || isCurrent}
                className={`w-full ${
                  isCurrent
                    ? "bg-slate-200 text-slate-500 cursor-default hover:bg-slate-200"
                    : plan.id === "pro"
                    ? "bg-violet-600 hover:bg-violet-700 text-white"
                    : plan.price_chf > 0
                    ? "bg-slate-900 hover:bg-slate-800 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`billing-upgrade-btn-${plan.id}`}
              >
                {upgrading === plan.id ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Wird vorbereitet …
                  </>
                ) : isCurrent ? (
                  "Aktueller Tarif"
                ) : plan.price_chf === 0 ? (
                  "Auf Free wechseln"
                ) : showProRated ? (
                  <>
                    <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                    Jetzt für CHF {quote.prorated_amount_chf.toFixed(0)} upgraden
                  </>
                ) : (
                  `Auf ${plan.name} upgraden`
                )}
              </Button>
              {showProRated && (
                <p className="text-[10px] text-emerald-700 text-center mt-1">
                  ✨ Anteilig für {quote.days_remaining} verbleibende Tage
                </p>
              )}
            </Card>
          );
        })}
      </div>

      {/* Iter96: Invoices / Zahlungs-Historie */}
      {invoices.length > 0 && (
        <Card className="p-5 border-slate-200" data-testid="billing-invoices-card">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="h-5 w-5 text-slate-600" />
            <h2 className="text-base font-semibold text-slate-900">Zahlungs-Historie</h2>
            <span className="text-xs text-slate-500 ml-1">({invoices.length})</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Datum</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Tarif</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Zyklus</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Betrag</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.session_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 text-slate-700 tabular-nums">{fmtDate(inv.created_at)}</td>
                    <td className="py-2 px-2 font-medium text-slate-900 capitalize">{inv.plan_id || "—"}</td>
                    <td className="py-2 px-2 text-slate-500">
                      {inv.billing_cycle === "yearly" ? "Jährlich" :
                       inv.billing_cycle === "prorated" ? "Anteilig" : "Monatlich"}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-mono">
                      CHF {inv.amount.toFixed(2)}
                    </td>
                    <td className="py-2 px-2">
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${
                        inv.payment_status === "paid"
                          ? "bg-emerald-100 text-emerald-700"
                          : inv.payment_status === "unpaid" || inv.payment_status === "pending"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        {inv.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Test-Mode Hinweis */}
      <Card className="p-3 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <span className="font-semibold">Stripe-Test-Modus:</span> Verwende Test-Kartennummer
            {" "}<code className="bg-white px-1.5 py-0.5 rounded font-mono text-[11px]">4242 4242 4242 4242</code>{" "}
            mit beliebigem zukünftigen Datum & CVC. Es wird kein echtes Geld abgebucht.
          </div>
        </div>
      </Card>
    </div>
  );
}
